import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EBM03230, EBM03360, EBM03362, type EbmProposal } from 'types/result';
import { clamp } from 'utils/number';
import { Checkbox } from '@shadcn/components/ui/checkbox';
import { MASKED_TEXT_CLASS, useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { CheckIcon } from '@radix-ui/react-icons';
import { AlertCircle } from 'lucide-react';
import { EbmProposalDurationInput } from './EbmProposals/EbmProposalDurationInput';
import { EbmProposalToolTipMessage } from './EbmProposals/EbmProposalToolTipMessage';
import { EbmProposalsRow } from 'widgets/results/EbmProposalsSection.styles';
import { SectionTitle } from 'widgets/results/ResultSection.styles';
import type { AutoSaveStatus } from 'hooks/useDebouncedFieldAutosave';
import { useEbmProposalsEditorContext } from 'providers/EbmProposalsEditorProvider';

type Props = {
  proposals?: EbmProposal[] | null;
  durationAutoStatus?: AutoSaveStatus;
  selectionAutoStatus?: AutoSaveStatus;
};

const HIDE_WHEN_EXCLUDED = new Set([EBM03362, EBM03360]);

const multiplierOf = (minutes: number) => Math.max(0, Math.floor(minutes / 10));
const isApplicable = (minutes: number) => multiplierOf(minutes) >= 1;

export function EbmProposalsSection({ proposals, durationAutoStatus, selectionAutoStatus }: Props) {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const { handleEbmSelectionMapChange, handleEbmDurationChange, onEbmCodeToggle } =
    useEbmProposalsEditorContext();
  const list = useMemo(() => (Array.isArray(proposals) ? proposals : []), [proposals]);

  // Currently only EBM03230 has a duration input.
  const [durations, setDurations] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    for (const p of list) {
      if ('durationMinutes' in p && p.durationMinutes != null) {
        initial[p.code] = clamp(p.durationMinutes, 1, 90);
      }
    }
    return initial;
  });

  const duration03230 = durations[EBM03230] ?? 10;

  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(() => new Set());
  const didInitSelectedCodeDefaultsRef = useRef(false);

  useEffect(() => {
    if (didInitSelectedCodeDefaultsRef.current || list.length === 0) return;
    didInitSelectedCodeDefaultsRef.current = true;

    setSelectedCodes((prev) => {
      const next = new Set(prev);
      for (const p of list) {
        if (p.excluded) continue;
        if (p.selected) next.add(p.code);
        else next.delete(p.code);
      }
      return next;
    });
  }, [list]);

  useEffect(() => {
    const selection: Record<string, boolean> = {};
    for (const p of list) {
      selection[p.code] = selectedCodes.has(p.code);
    }

    handleEbmSelectionMapChange(selection);
  }, [selectedCodes, list, handleEbmSelectionMapChange]);

  const setCodeSelected = (code: string, checked: boolean) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });
    onEbmCodeToggle();
  };

  const handleCheckboxChange = (code: string, checked: boolean) => {
    const proposal = list.find((p) => p.code === code);
    if (proposal?.excluded) return;
    trackEvent(ANALYTICS_EVENTS.RESULT_EBM_CODE_TOGGLED, { checked });
    setCodeSelected(code, checked);
  };

  const handleDurationChange = (minutes: number) => {
    const becameApplicable = isApplicable(minutes);
    const wasApplicable = isApplicable(duration03230);
    setDurations((prev) => ({ ...prev, [EBM03230]: minutes }));
    handleEbmDurationChange(minutes);

    if (becameApplicable === wasApplicable) return;
    if (list.find((p) => p.code === EBM03230)?.excluded) return;
    setCodeSelected(EBM03230, becameApplicable);
  };

  const rows = useMemo(() => {
    const multiplier03230 = multiplierOf(duration03230);
    return list.map((proposal) => {
      const excluded = Boolean(proposal.excluded);
      const is03230 = proposal.code === EBM03230;
      const applicable = is03230 ? isApplicable(duration03230) && !excluded : !excluded;
      const inclusionMessage =
        proposal.code === EBM03362
          ? t(`result.ebmProposals.inclusionMessage.${proposal.inclusionReason}`, {
              defaultValue: '',
            })
          : undefined;
      const exclusionMessages =
        excluded && proposal.code === EBM03230
          ? (proposal.exclusions ?? []).map((e) =>
              t('result.ebmProposals.exclusionMessage', {
                code: proposal.code,
                conflictingCode: e.conflictingCode,
                scope: t(`result.ebmProposals.exclusionScope.${e.scope}`, {
                  defaultValue: e.scope,
                }),
              })
            )
          : [];
      return {
        code: proposal.code,
        description: proposal.description,
        multiplier: is03230 ? multiplier03230 : undefined,
        applicable,
        excluded,
        exclusionMessages,
        selected: selectedCodes.has(proposal.code),
        inclusionMessage,
      };
    });
  }, [duration03230, list, selectedCodes, t]);

  if (!list.length) return null;

  return (
    <section>
      <header className="mb-2 flex items-center gap-2">
        <SectionTitle>{t('result.ebmProposals.title')}</SectionTitle>
      </header>

      <div className="space-y-2">
        {rows.map((row) =>
          !row.excluded || !HIDE_WHEN_EXCLUDED.has(row.code) ? (
            <EbmProposalsRow key={row.code}>
              <div
                className={`flex w-full items-center gap-3 overflow-hidden rounded-md px-3 py-2 text-sm ${
                  row.excluded ? 'opacity-60' : ''
                }`}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <Checkbox
                    checked={row.selected && row.applicable}
                    onCheckedChange={(value) => handleCheckboxChange(row.code, value === true)}
                    disabled={!row.applicable}
                    className="border border-muted-foreground bg-muted"
                    aria-label={t('result.ebmProposals.checkboxAriaLabel')}
                    data-ph-no-autocapture
                  />

                  <span
                    className={`shrink-0 text-card-foreground ${MASKED_TEXT_CLASS} ${
                      row.excluded ? 'line-through' : ''
                    }`}
                  >
                    {row.code}
                  </span>
                  <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <span
                      className={`min-w-0 truncate text-card-foreground ${MASKED_TEXT_CLASS} ${
                        row.excluded ? 'line-through' : ''
                      }`}
                      title={t(`result.ebmProposals.descriptions.${row.description}`)}
                    >
                      {t(`result.ebmProposals.descriptions.${row.description}`)}
                    </span>
                    {row.inclusionMessage && (
                      <EbmProposalToolTipMessage inclusionMessage={row.inclusionMessage} />
                    )}
                  </div>
                </div>
                {row.code === EBM03230 && (
                  <EbmProposalDurationInput
                    duration={duration03230}
                    onDurationChange={handleDurationChange}
                  />
                )}
              </div>
              {row.code === EBM03230 && row.excluded && row.exclusionMessages.length > 0 && (
                <div className="ml-9 mr-3 space-y-1">
                  {row.exclusionMessages.map((message, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                      role="status"
                    >
                      <AlertCircle className="size-4 shrink-0 text-amber-600" aria-hidden />
                      <span className={MASKED_TEXT_CLASS}>{message}</span>
                    </div>
                  ))}
                </div>
              )}
            </EbmProposalsRow>
          ) : null
        )}
      </div>
      {(durationAutoStatus === 'saved' || selectionAutoStatus === 'saved') && (
        <span
          className="flex items-center justify-end gap-1 text-xs text-success mt-1 px-3"
          title="Autosaved"
        >
          <CheckIcon className="h-4 w-4" />
          <span>{t('result.autoSaveSuccessful')}</span>
        </span>
      )}
    </section>
  );
}
